import React, { useEffect, useState } from "react";
import { useToastContext } from "@finitless/shared/contexts";
import {
  Icon,
  Modal,
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "../../";
import { Pagination } from "../../Data";
import { useTaxes } from "../../../../hooks";
import TaxFormModal from "./TaxFormModal";

const TaxesStep = ({
  businessId,
  isUpdatingProducts = false,
  selectedTaxId,
  onAssigned,
}) => {
  const toast = useToastContext();
  const [showForm, setShowForm] = useState(false);
  const [editingTax, setEditingTax] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [currentTaxId, setCurrentTaxId] = useState(selectedTaxId ?? null);

  const {
    taxes,
    pagination,
    page,
    setPagination,
    isLoading,
    isFetching,
    createTax,
    isCreating,
    updateTax,
    isUpdating,
    deleteTax,
    isDeleting,
    assignTaxToBusiness,
    isAssigning,
  } = useTaxes({ pageSize: 10 });

  const isBusy =
    isLoading ||
    isFetching ||
    isCreating ||
    isUpdating ||
    isDeleting ||
    isAssigning;

  useEffect(() => {
    setCurrentTaxId(selectedTaxId ?? null);
  }, [selectedTaxId]);

  const handleCreate = async (payload) => {
    try {
      const res = await createTax(payload);
      setShowForm(false);
      if (res?.result?.id) {
        toast.success("Tax created");
      }
    } catch (e) {
      console.error("Create tax failed", e);
    }
  };

  const handleEdit = async (payload) => {
    try {
      await updateTax({ taxId: editingTax.id, payload });
      setEditingTax(null);
    } catch (e) {
      console.error("Update tax failed", e);
    }
  };

  const handleAssign = async (tax) => {
    try {
      const unassign = currentTaxId === tax.id;
      if (!isUpdatingProducts) {
        await assignTaxToBusiness({
          businessId,
          taxId: unassign ? null : tax.id,
        });
      }
      setCurrentTaxId(unassign ? null : tax.id);
      if (onAssigned) onAssigned(unassign ? null : tax);
      if (unassign) {
        toast.success("Tax unassigned");
      } else {
        toast.success("Tax assigned");
      }
    } catch (e) {
      console.error("Assign tax failed", e);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteTax(confirmDelete.id);
      setConfirmDelete(null);
    } catch (e) {
      console.error("Delete tax failed", e);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-medium text-white sm:text-lg">Taxes</h3>
          <p className="text-xs text-gray-400 sm:text-sm">
            Select a tax for this business or manage your taxes.
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-1 rounded bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700"
        >
          <Icon name="Plus" className="h-4 w-4" /> Add Tax
        </button>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-700 bg-gray-800/60 shadow-lg shadow-black/20">
        <div className="grid grid-cols-12 border-b border-gray-700 bg-gradient-to-r from-gray-800 to-gray-700/60 px-4 py-3 text-xs text-gray-300 sm:text-sm">
          <div className="col-span-4">Name</div>
          <div className="col-span-1">Rate</div>
          <div className="col-span-3">Type</div>
          <div className="col-span-2">Custom rates</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>

        {isLoading ? (
          <>
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="grid animate-pulse grid-cols-12 items-center border-t border-gray-700 px-4 py-3"
              >
                <div className="col-span-4">
                  <div className="mb-2 h-4 w-3/4 rounded bg-gray-700"></div>
                  <div className="h-3 w-1/2 rounded bg-gray-700"></div>
                </div>
                <div className="col-span-1">
                  <div className="h-6 w-12 rounded bg-gray-700"></div>
                </div>
                <div className="col-span-3">
                  <div className="h-6 w-[90%] rounded bg-gray-700"></div>
                </div>
                <div className="col-span-2">
                  <div className="h-6 w-8 rounded bg-gray-700"></div>
                </div>
                <div className="col-span-2 flex items-center justify-end gap-2">
                  <div className="h-7 w-16 rounded bg-gray-700"></div>
                  <div className="h-7 w-12 rounded bg-gray-700"></div>
                  <div className="h-7 w-14 rounded bg-gray-700"></div>
                </div>
              </div>
            ))}
          </>
        ) : taxes.length === 0 ? (
          <div className="p-8 text-center text-gray-300">No taxes found</div>
        ) : (
          taxes.map((t) => {
            const isSelected = currentTaxId === t.id;
            const customCount = ["delivery", "pickup"].filter(
              (k) => !!t.order_type_rates?.[k],
            ).length;
            return (
              <div
                key={t.id}
                className={`grid grid-cols-12 items-center border-t px-4 py-3 text-white transition-colors ${
                  isSelected
                    ? "border-green-500/30 bg-gradient-to-r from-green-500/10 to-blue-500/10 shadow-lg shadow-green-500/10"
                    : "border-gray-700 hover:bg-gray-700/40"
                }`}
              >
                <div className="col-span-4">
                  <div className="font-medium">{t.name}</div>
                  <div className="truncate text-xs text-gray-400">
                    {t.description || "—"}
                  </div>
                </div>
                <div className="col-span-1">
                  <span className="rounded border border-gray-600 bg-gray-700 px-2 py-0.5 text-xs">
                    {t.rate}%
                  </span>
                </div>
                <div className="col-span-3">
                  <span
                    className={`block w-[90%] rounded border px-2 py-0.5 text-center text-xs ${t.type === 1 ? "border-blue-500/40 bg-blue-500/20 text-blue-300" : "border-purple-500/40 bg-purple-500/20 text-purple-300"}`}
                  >
                    {t.type === 1 ? "Included" : "Not included"}
                  </span>
                </div>
                <div className="col-span-2">
                  {customCount > 0 ? (
                    <HoverCard>
                      <HoverCardTrigger asChild>
                        <button className="rounded border border-gray-600 bg-gray-700 px-2 py-0.5 text-xs hover:bg-gray-600">
                          {customCount}
                        </button>
                      </HoverCardTrigger>
                      <HoverCardContent side="top" className="w-64">
                        <div className="mb-2 text-sm text-gray-200">
                          Custom rates
                        </div>
                        <div className="space-y-1 text-xs text-gray-300">
                          {t.order_type_rates?.delivery && (
                            <div className="flex items-center justify-between">
                              <span>Delivery</span>
                              <span className="rounded border border-gray-600 bg-gray-700 px-2 py-0.5">
                                {t.order_type_rates.delivery.rate}% (
                                {t.order_type_rates.delivery.type === 1
                                  ? "Included"
                                  : "Not included"}
                                )
                              </span>
                            </div>
                          )}
                          {t.order_type_rates?.pickup && (
                            <div className="flex items-center justify-between">
                              <span>Pickup</span>
                              <span className="rounded border border-gray-600 bg-gray-700 px-2 py-0.5">
                                {t.order_type_rates.pickup.rate}% (
                                {t.order_type_rates.pickup.type === 1
                                  ? "Included"
                                  : "Not included"}
                                )
                              </span>
                            </div>
                          )}
                        </div>
                      </HoverCardContent>
                    </HoverCard>
                  ) : (
                    <span className="rounded border border-gray-600 bg-gray-700 px-2 py-0.5 text-xs">
                      0
                    </span>
                  )}
                </div>
                <div className="col-span-2 flex items-center justify-end gap-2">
                  <button
                    onClick={() => handleAssign(t)}
                    disabled={isBusy}
                    className={`rounded border px-2 py-1 text-xs transition-colors ${isSelected ? "border-red-500 bg-red-600 hover:bg-red-500" : "border-gray-600 bg-gray-800 hover:bg-gray-700"}`}
                    title={
                      isSelected
                        ? "Unassign from business"
                        : "Assign to business"
                    }
                  >
                    {isSelected ? "Unassign" : "Assign"}
                  </button>
                  <button
                    onClick={() => setEditingTax(t)}
                    disabled={isBusy}
                    className="rounded border border-gray-600 bg-gray-800 px-2 py-1 text-xs hover:bg-gray-700"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setConfirmDelete(t)}
                    disabled={isBusy}
                    className="rounded border border-red-500/60 px-2 py-1 text-xs text-red-300 hover:bg-red-900/30"
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination (match ProductsIndex.jsx) */}
      {pagination?.total_pages > 1 && (
        <div className="mt-4">
          <div className="rounded-lg border border-gray-700 bg-gray-800 p-4">
            <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
              <div className="text-sm text-gray-400">
                {(() => {
                  const pageSizeLocal = pagination?.page_size || 10;
                  const total = pagination?.total || 0;
                  const currentPage = page || 1;
                  const start = total
                    ? (currentPage - 1) * pageSizeLocal + 1
                    : 0;
                  const end = Math.min(currentPage * pageSizeLocal, total);
                  return (
                    <>
                      Showing{" "}
                      <span className="font-medium text-white">{start}</span> to{" "}
                      <span className="font-medium text-white">{end}</span> of{" "}
                      <span className="font-medium text-white">{total}</span>{" "}
                      taxes
                    </>
                  );
                })()}
              </div>

              <Pagination
                currentPage={page}
                totalPages={pagination.total_pages}
                onPageChange={setPagination}
                maxVisiblePages={5}
              />
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal (single instance) */}
      {(showForm || editingTax) && (
        <TaxFormModal
          isOpen={true}
          onClose={() => {
            if (editingTax) setEditingTax(null);
            if (showForm) setShowForm(false);
          }}
          onSubmit={editingTax ? handleEdit : handleCreate}
          initialValues={editingTax || undefined}
          isSubmitting={editingTax ? isUpdating : isCreating}
        />
      )}

      {/* Delete Confirm (match AccountPage modal style) */}
      {confirmDelete && (
        <Modal
          isOpen={true}
          onClose={() => setConfirmDelete(null)}
          title="Delete Tax"
          subtitle="Are you sure you want to delete this tax?"
          maxWidth="max-w-md"
        >
          <div className="space-y-4">
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
              <div className="flex items-center space-x-3">
                <Icon name="AlertTriangle" className="h-5 w-5 text-red-500" />
                <div>
                  <h4 className="text-sm font-medium text-red-800 dark:text-red-200">
                    {confirmDelete.name}
                  </h4>
                  <p className="mt-1 text-xs text-red-600 dark:text-red-300">
                    This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>

            <p className="text-muted-foreground text-sm">
              Deleting this tax will permanently remove it from your account.
            </p>

            <div className="flex space-x-3 pt-4">
              <button
                onClick={() => setConfirmDelete(null)}
                disabled={isDeleting}
                className="text-card-foreground bg-card border-border hover:bg-accent flex-1 rounded-lg border px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="flex flex-1 items-center justify-center space-x-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isDeleting ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                ) : null}
                <span>{isDeleting ? "Deleting..." : "Delete Tax"}</span>
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default TaxesStep;
